import moment from "moment";
import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import logDeviceIcon from "../../assets/images/site-log-device.svg";
import {
  batteryStatusTypeText,
  EBatteryStatusType,
} from "../../enums/battery-status-type";
import { IBatteryInverterView } from "../../interfaces/battery-inverter-view";
import { IGridInverterView } from "../../interfaces/grid-inverter-view";
import Gauge from "../../ui-elements/gauge/gauge";
import InverterInformation from "../../ui-elements/inverter-information/inverter-information";
import MaterialIcon from "../../ui-elements/material-icon/material-icon";
import Helper from "../../utils/helpers";
import styles from "./inverter-info-card.module.scss";

interface IProps {
  gridInverter?: IGridInverterView;
  batteryInverter?: IBatteryInverterView;
}

const InverterInfoCard = (props: IProps) => {
  const { gridInverter, batteryInverter } = props;

  const [collapsed, setCollapsed] = useState(false);

  return (
    <div
      className={`${styles.infoCardContainer} p-4 cursor-pointer`}
      onClick={() => setCollapsed((ps) => !ps)}
    >
      <Row className="align-items-center justify-content-between">
        <Col>
          <Row className="align-items-center">
            <Col className="col-auto pe-0">
              <img src={logDeviceIcon} alt="Device Icon" />
            </Col>
            <Col className="text-dark font-weight-500 font-size-14">
              {gridInverter?.modelType ?? batteryInverter?.modelType}
            </Col>
          </Row>
        </Col>
        <Col className="col-auto">
          <MaterialIcon
            icon={collapsed ? "keyboard_arrow_down" : "keyboard_arrow_right"}
            color="#AEBBD0"
            size={25}
          />
        </Col>
      </Row>
      {collapsed && (
        <Row className="align-items-center justify-content-between my-1 g-4">
          <Col className="col-12 col-lg-auto">
            <Row className="justify-content-center justify-content-lg-start">
              <Col className="col-auto">
                <Gauge
                  icon="solar_power"
                  value={
                    gridInverter?.dailyGeneration ??
                    Number(batteryInverter?.dailyGeneration)
                  }
                  maxValue={
                    gridInverter?.capacity ?? Number(batteryInverter?.capacity)
                  }
                  circleRatio={1}
                  unit={"kWh"}
                  trailColor="#D08700"
                  width={150}
                  height={75}
                  isDifferentStyles
                />
              </Col>
            </Row>
            <Row className="justify-content-center mt-5 pt-5">
              <Col className="col-auto text-light font-weight-500 font-size-16">
                Today Generation
              </Col>
            </Row>
          </Col>
          <Col className="col-12 col-sm-6 col-lg-auto">
            <InverterInformation
              inverterInfo={[
                {
                  title: "Model",
                  value: gridInverter?.modelType ?? batteryInverter?.modelType,
                },
                {
                  title: "SN",
                  value:
                    gridInverter?.inverterSN ?? batteryInverter?.inverterSn,
                },
                {
                  title: "Check Code",
                  value: gridInverter?.checkCode ?? batteryInverter?.checkCode,
                },
                {
                  title: "Capacity",
                  value: `${
                    gridInverter?.capacity ?? batteryInverter?.capacity
                  } kW`,
                },
                {
                  title: `${
                    gridInverter?.connected ? "Connected" : "Battery Health"
                  }`,
                  value: gridInverter?.connected
                    ? moment(gridInverter?.connected)
                        .utcOffset("+05:30")
                        .format("YYYY-MM-DD HH:mm")
                    : batteryInverter?.soh,
                },
              ]}
            />
          </Col>
          <Col className="col-12 col-sm-6 col-lg-auto">
            <InverterInformation
              inverterInfo={[
                {
                  title: "Inner Temp",
                  value: (
                    <>
                      {gridInverter?.innerTemperature ??
                        batteryInverter?.innerTemperature}{" "}
                      C&deg;
                    </>
                  ),
                },
                {
                  title: `${
                    gridInverter?.gridPower ? `Power` : `Output Power`
                  }`,
                  value: `${
                    gridInverter?.gridPower ?? batteryInverter?.outputPower
                  } kW`,
                },
                {
                  title: `${
                    gridInverter?.acCurrent1 ? `AC Current` : `Output Voltage`
                  }`,
                  value: `${
                    gridInverter?.acCurrent1 ?? batteryInverter?.outputVoltage
                  } A`,
                },
                {
                  title: `${
                    gridInverter?.acVoltage1 ? `AC Voltage` : `Battery Status`
                  }`,
                  value: gridInverter?.acVoltage1
                    ? `${gridInverter?.acVoltage1} V`
                    : batteryStatusTypeText[
                        batteryInverter?.batteryStatus as EBatteryStatusType
                      ],
                },
                {
                  title: `${
                    gridInverter?.acFrequency1 ? `AC Frequency` : `Battery`
                  }`,
                  value: gridInverter?.acFrequency1
                    ? `${gridInverter?.acFrequency1} Hz`
                    : `${batteryInverter?.batteryV}/${batteryInverter?.batteryA}/${batteryInverter?.batteryW}(V/A/W)`,
                },
              ]}
            />
          </Col>
          <Col className="col-12 col-xxl-auto">
            <Row className="g-4">
              <Col className={` col-12 col-sm-6 col-xxl-12`}>
                <div className={styles.background1}>
                  <div className="text-light font-weight-500 font-size-16">
                    Total Generation
                  </div>
                  <div className="text-dark font-weight-500 font-size-16 mt-2">
                    {Helper.roundTo2(
                      gridInverter?.totalGeneration ??
                        Number(batteryInverter?.totalGeneration)
                    )}{" "}
                    kWh
                  </div>
                </div>
              </Col>
              <Col className={` col-12 col-sm-6 col-xxl-12`}>
                <div className={styles.background1}>
                  <div className="text-light font-weight-500 font-size-16">
                    {gridInverter?.hourTotal
                      ? `Total Hours`
                      : `Battery Percentage`}
                  </div>
                  <div className="text-dark font-weight-500 font-size-16 mt-2">
                    {gridInverter?.hourTotal
                      ? `${gridInverter?.hourTotal} H`
                      : `${batteryInverter?.soc}%`}
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default InverterInfoCard;
